import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../common/Button';
import { AuthCTA } from './AuthCTA';
import { getEmailValidationKey } from '../../utils/authErrors';

interface SignInFormProps {
  onSubmit: (email: string, password: string) => Promise<string | undefined>;
}

export default function SignInForm({ onSubmit }: SignInFormProps) {
  const { t } = useTranslation(['common', 'auth', 'home']);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [show, setShow] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailValidationKey = getEmailValidationKey(email);
    if (emailValidationKey) {
      setError(t(emailValidationKey, { ns: 'auth' }));
      return;
    }

    const errorMessage = await onSubmit(email, password);

    if (errorMessage) {
      setError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="flex flex-col md:flex-row md:min-h-[calc(100vh-4rem)]">
        {/* Sign In Form */}
        <div className="w-full md:w-1/2 order-1 md:order-2 pt-11 pb-8 md:px-8 md:flex">
          <div className="max-w-md w-full mx-auto">
            <h1 className="text-secondary [font-feature-settings:'liga'_off,'clig'_off] font-ambit text-[1.5625rem] font-[900] leading-[1.875rem] mb-2">
              {t('signIn', { ns: 'auth' })}
            </h1>
            <p className="text-[#3B5266] [font-feature-settings:'liga'_off,'clig'_off] font-ambit text-[1.0625rem] font-normal leading-[1.5625rem] mb-1">
              {t('noAccount', { ns: 'auth' })}{' '}
            </p>
            <Link
              to="/signup"
              className="text-[#3B5266] [font-feature-settings:'liga'_off,'clig'_off] font-ambit text-[1.0625rem] font-[700] leading-[1.5625rem] underline decoration-solid decoration-[#3B5266]"
            >
              {t('signUpCta', { ns: 'auth' })}
            </Link>

            <form onSubmit={handleSubmit} className="mt-8" noValidate>
              {error && (
                <div
                  className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded-md relative"
                  role="alert"
                >
                  <span className="block sm:inline">{error}</span>
                </div>
              )}

              <div className="mt-4">
                <label
                  htmlFor="email"
                  className="text-[#3B5266] [font-feature-settings:'liga'_off,'clig'_off] font-ambit text-[0.9375rem] font-normal leading-[1.25rem]"
                >
                  {t('email', { ns: 'auth' })}
                </label>
                <div className="relative mt-2">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-secondary-light" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={e => {
                      setEmail(e.target.value);
                      if (error) setError('');
                    }}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md bg-[#E1E9F0] focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder={t('emailPlaceholder', { ns: 'auth' })}
                    required
                  />
                </div>
              </div>

              <div className="mt-4">
                <label
                  htmlFor="password"
                  className="text-[#3B5266] [font-feature-settings:'liga'_off,'clig'_off] font-ambit text-[0.9375rem] font-normal leading-[1.25rem]"
                >
                  {t('password', { ns: 'auth' })}
                </label>
                <div className="relative mt-2">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-secondary-light" />
                  </div>
                  <input
                    id="password"
                    type={show ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md bg-[#E1E9F0] focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder={t('passwordPlaceholder', { ns: 'auth' })}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-secondary-light"
                    onClick={() => setShow(v => !v)}
                    tabIndex={-1}
                  >
                    {show ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <Button type="submit" variant="primary" className="w-full mt-8">
                {t('signInCta', { ns: 'auth' })}
              </Button>

              <div className="flex items-center justify-center md:justify-start mt-8">
                <Link
                  to="/forgot-password"
                  className="text-[#3B5266] [font-feature-settings:'liga'_off,'clig'_off] font-ambit text-[1.0625rem] font-[700] leading-[1.5625rem] underline decoration-solid decoration-[#3B5266]"
                >
                  {t('forgotPassword', { ns: 'auth' })}
                </Link>
              </div>
            </form>
          </div>
        </div>
        {/* AuthCTA - always visible, after form on mobile, left on desktop */}
        <AuthCTA
          slogan={t('slogan', { ns: 'home' })}
          ctaTitle={t('ctaTitle', { ns: 'home' })}
          ctaSubtitle={t('ctaSubtitle', { ns: 'home' })}
          buttonText={t('discoverButton', { ns: 'home' })}
        />
      </div>
    </div>
  );
}
